import { DeleteOutlined, HolderOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import type { FormListFieldData, FormListOperation } from 'antd'
import { Button, Checkbox, Divider, Flex, Form, InputNumber, Slider, Tooltip, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { MessageTemplate } from '../../message-template'
import { WhatsappBusinessAccount } from '../../../whatsapp-business-account'
import { PhoneNumber } from '../../../whatsapp-business-account/phone-number'
import { PhoneNumberSelect } from '../create/steps/phone-number-select'
import { TemplateSelect } from '../create/steps/template-select'
import { WhatsappBusinessAccountSelect } from '../create/steps/whatsapp-business-account-select'
import { FormItem } from '../../../components/form-item'
import { useTemplateLanguageNotice } from '../../template-language-notice'

const quietHoursMarks = {
    1: '01:00',
    7: '07:00',
    12: '12:00',
    18: '18:00',
    23: '23:00',
}

function timeToHour(value?: string | null, fallback = 0) {
    if (!value) {
        return fallback
    }

    const [hours = fallback] = value.split(':').map(Number)

    return Number.isFinite(hours) ? hours : fallback
}

function hourToTime(value: number) {
    return `${String(value).padStart(2, '0')}:00`
}

const MAX_RECONTACT_STEPS = 5

type RecontactStepRowProps = {
    field: FormListFieldData
    index: number
    templates: MessageTemplate[]
    loading: boolean
    disabled: boolean
    onRemove: (name: number) => void
}

function RecontactStepRow({ field, index, templates, loading, disabled, onRemove }: RecontactStepRowProps) {
    const { t } = useTranslation()
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id: field.key })

    return (
        <Flex
            ref={setNodeRef}
            style={{
                transform: CSS.Translate.toString(transform),
                opacity: isDragging ? 0.5 : 1,
            }}
            gap="middle"
            align="flex-start"
            className="rounded border border-solid border-gray-200 p-3"
        >
            <Button
                aria-label={t('Reorder')}
                type="text"
                icon={<HolderOutlined />}
                className="mt-8 h-8 w-8 shrink-0 cursor-grab p-0 text-gray-400 hover:text-gray-600"
                {...attributes}
                {...listeners}
            />
            <Flex
                align="center"
                justify="center"
                className="mt-8 h-8 w-8 shrink-0 rounded-full bg-gray-100 text-sm font-medium text-gray-700"
            >
                {index + 1}
            </Flex>
            <Flex
                gap="middle"
                className="w-full min-w-0 flex-col md:flex-row"
            >
                <FormItem
                    label={<DelayLabel />}
                    name={[field.name, 'delayMinutes']}
                    className="md:w-[180px]"
                    rules={[{ required: true }]}
                >
                    <InputNumber
                        min={5}
                        step={5}
                        addonAfter={t('min')}
                        className="w-full"
                    />
                </FormItem>
                <FormItem
                    label={t('Template')}
                    name={[field.name, 'messageTemplateId']}
                    className="w-full"
                    normalize={(value) => value ?? null}
                    rules={[{ required: true }]}
                >
                    <TemplateSelect
                        allowClear
                        templates={templates}
                        loading={loading}
                        disabled={disabled}
                    />
                </FormItem>
            </Flex>
            <Button
                aria-label={t('Remove')}
                className="mt-8"
                icon={<DeleteOutlined />}
                danger
                onClick={() => onRemove(field.name)}
            />
        </Flex>
    )
}

function DelayLabel() {
    const { t } = useTranslation()

    return (
        <Flex
            align="center"
            gap={6}
        >
            {t('Delay')}
            <Tooltip
                title={t(
                    'Each delay is counted after the previous outgoing message. The next template is only sent when the candidate has not answered.'
                )}
            >
                <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
        </Flex>
    )
}

type RecontactTimelineProps = {
    templates: MessageTemplate[]
    loading: boolean
    disabled: boolean
}

function RecontactTimeline({ templates, loading, disabled }: RecontactTimelineProps) {
    const { t } = useTranslation()

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragEnd =
        (fields: FormListFieldData[], move: FormListOperation['move']) =>
        ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) {
                return
            }

            const oldIndex = fields.findIndex((field) => field.key === active.id)
            const newIndex = fields.findIndex((field) => field.key === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                move(oldIndex, newIndex)
            }
        }

    return (
        <FormItem label={t('Recontact timeline')}>
            <Form.List name="recontactSchedule">
                {(fields, { add, remove, move }) => (
                    <Flex
                        vertical
                        gap="middle"
                    >
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            modifiers={[restrictToVerticalAxis]}
                            onDragEnd={handleDragEnd(fields, move)}
                        >
                            <SortableContext
                                items={fields.map((field) => field.key)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Flex
                                    vertical
                                    gap="middle"
                                >
                                    {fields.map((field, index) => (
                                        <RecontactStepRow
                                            key={field.key}
                                            field={field}
                                            index={index}
                                            templates={templates}
                                            loading={loading}
                                            disabled={disabled}
                                            onRemove={remove}
                                        />
                                    ))}
                                </Flex>
                            </SortableContext>
                        </DndContext>

                        <Tooltip
                            title={
                                fields.length >= MAX_RECONTACT_STEPS
                                    ? t('You can add up to 5 recontact steps')
                                    : undefined
                            }
                        >
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                disabled={disabled || fields.length >= MAX_RECONTACT_STEPS}
                                onClick={() => add({ delayMinutes: 30, messageTemplateId: undefined })}
                            >
                                {t('Add recontact step')}
                            </Button>
                        </Tooltip>
                    </Flex>
                )}
            </Form.List>
        </FormItem>
    )
}

function QuietHoursSettings() {
    const { t } = useTranslation()

    return (
        <Form.Item
            shouldUpdate
            noStyle
        >
            {({ getFieldValue, setFieldValue }) => {
                const recontactSchedule = getFieldValue('recontactSchedule') || []
                const quietHoursEnabled = getFieldValue('recontactQuietHoursEnabled')

                if (recontactSchedule.length === 0) {
                    return null
                }

                const quietStart = timeToHour(getFieldValue('recontactQuietHoursStart'), 23)
                const quietEnd = timeToHour(getFieldValue('recontactQuietHoursEnd'), 7)
                const value: [number, number] = [quietEnd, quietStart]

                return (
                    <section aria-label={t('Quiet hours')}>
                        <Divider />

                        <Typography.Title level={5}>{t('Quiet hours')}</Typography.Title>

                        <FormItem
                            name="recontactQuietHoursEnabled"
                            valuePropName="checked"
                        >
                            <Checkbox>{t('Do not send recontacts during quiet hours')}</Checkbox>
                        </FormItem>

                        <Form.Item
                            name="recontactQuietHoursStart"
                            hidden
                        />
                        <Form.Item
                            name="recontactQuietHoursEnd"
                            hidden
                        />

                        {quietHoursEnabled && (
                            <>
                                <Slider
                                    range
                                    min={0}
                                    max={24}
                                    step={1}
                                    marks={quietHoursMarks}
                                    value={value}
                                    included={false}
                                    tooltip={{
                                        formatter: (value) => (value === undefined ? null : hourToTime(value)),
                                    }}
                                    onChange={([allowedStart, allowedEnd]) => {
                                        setFieldValue('recontactQuietHoursStart', hourToTime(allowedEnd))
                                        setFieldValue('recontactQuietHoursEnd', hourToTime(allowedStart))
                                    }}
                                />
                                <Flex
                                    vertical
                                    gap={2}
                                >
                                    <Typography.Text>
                                        {t('Send recontacts between')} {hourToTime(value[0])} {t('and')}{' '}
                                        {hourToTime(value[1])}
                                    </Typography.Text>
                                    <Typography.Text type="secondary">
                                        {t('Quiet from')} {hourToTime(quietStart)} {t('Until').toLowerCase()}{' '}
                                        {hourToTime(quietEnd)}
                                    </Typography.Text>
                                </Flex>
                            </>
                        )}
                    </section>
                )
            }}
        </Form.Item>
    )
}

type CampaignWhatsappSectionProps = {
    accounts: WhatsappBusinessAccount[]
    accountsLoading: boolean
    phoneNumbers: PhoneNumber[]
    phoneNumbersLoading: boolean
    selectedWhatsappBusinessAccountId?: string | null
    startTemplates: MessageTemplate[]
    startTemplatesLoading: boolean
    recontactTemplates: MessageTemplate[]
    recontactTemplatesLoading: boolean
    onWhatsappBusinessAccountChange: () => void
    /** For a screening created from a website: the website's default start message, to show when this one differs. */
    website?: { siteName: string; defaultStartMessageTemplateId?: string | null } | null
}

export function CampaignWhatsappSection({
    accounts,
    accountsLoading,
    phoneNumbers,
    phoneNumbersLoading,
    selectedWhatsappBusinessAccountId,
    startTemplates,
    startTemplatesLoading,
    recontactTemplates,
    recontactTemplatesLoading,
    onWhatsappBusinessAccountChange,
    website,
}: CampaignWhatsappSectionProps) {
    const { t } = useTranslation()

    const form = Form.useFormInstance()
    const startId = Form.useWatch('startMessageTemplateId', form)
    const templateLanguageNotice = useTemplateLanguageNotice(startTemplates.find((template) => template.id === startId))

    const noAccount = !selectedWhatsappBusinessAccountId

    return (
        <>
            <FormItem
                label={t('WhatsApp business account')}
                name="whatsappBusinessAccountId"
                normalize={(value) => value ?? null}
            >
                <WhatsappBusinessAccountSelect
                    allowClear
                    accounts={accounts}
                    loading={accountsLoading}
                    onChange={onWhatsappBusinessAccountChange}
                />
            </FormItem>

            <FormItem
                label={t('Phone number')}
                name="phoneNumberId"
                normalize={(value) => value ?? null}
            >
                <PhoneNumberSelect
                    allowClear
                    phoneNumbers={phoneNumbers}
                    loading={phoneNumbersLoading}
                    disabled={noAccount}
                />
            </FormItem>

            <FormItem
                label={t('Start message')}
                name="startMessageTemplateId"
                normalize={(value) => value ?? null}
                extra={
                    <>
                        {templateLanguageNotice}
                        {website?.defaultStartMessageTemplateId &&
                            startId &&
                            startId !== website.defaultStartMessageTemplateId && (
                                <Typography.Text
                                    type="secondary"
                                    className="block text-xs"
                                >
                                    {t(
                                        'Different from the default of {{site}}. Vacancies of the website that you switch on later use the default.',
                                        {
                                            site: website.siteName,
                                        }
                                    )}
                                </Typography.Text>
                            )}
                    </>
                }
            >
                <TemplateSelect
                    allowClear
                    templates={startTemplates}
                    loading={startTemplatesLoading}
                    disabled={noAccount}
                />
            </FormItem>

            <RecontactTimeline
                templates={recontactTemplates}
                loading={recontactTemplatesLoading}
                disabled={noAccount}
            />

            <QuietHoursSettings />
        </>
    )
}
